import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  ComposableMap,
  ZoomableGroup,
  Geographies,
  Geography,
} from 'react-simple-maps';

import * as d3 from 'd3';
import CircularProgress from '@material-ui/core/CircularProgress';
import WarningIcon from '@material-ui/icons/Warning';

import MapTooltip from '../components/MapTooltip';

import MapLegend from '../components/mapLegend';
import MapOffshoreIcon from '../components/mapOffshoreIcon';

const styles = (theme) => ({
  composableMap: {
    width: '100%',
  },
  leftTableColumn: {
    width: '230px',
    textAlign: 'left',
    fontFamily: 'Flanders Art Sans!important',
  },
  rightTableColumn: {
    textAlign: 'right',
    fontFamily: 'Flanders Art Sans!important',
  },
  toolTip: {
    opacity: '0.7!important',
    fontFamily: 'Flanders Art Sans!important',
    color: 'black!important',
  },
  wrapperStyles: {
    position: 'relative',
    display: 'flex',
    flexGrow: 1,
    paddingTop: 48, // reserving some space for the legend
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '100%',
    },
  },
  limitedFunctionalityWarning: {
    fontSize: '12pt',
    color: '#f77!important',
    fontFamily: 'Flanders Art Sans!important',
    textAlign: 'center',
    width: '100%',
  },
  offshoreIcon: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
});

let isIE = /*@cc_on!@*/ false || !!document.documentMode;
let mobileCheck = false;
window.mobileCheck = function () {
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      mobileCheck = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return mobileCheck;
};

class EnergyMapFlanders extends Component {
  constructor() {
    super();

    this.state = {
      populationData: true,
      valueScale: d3.scaleLinear().clamp(true),
      colorScale: d3.scaleLinear().interpolate(d3.interpolateRgb),
      unitHour: 'Wh',
      unitMonth: 'Wh',
      componentUpdated: false,
      dataReady: false,
      currentTech: {
        solar: true,
        wind: false,
        windOffshore: false,
      },
      currentFilter: 'energy',
      currentMode: 'realtime',
      hasForecast: false,

      isTooltipActive: {},
      allTooltipsFalse: {},
    };

    this.updateMapData = this.updateMapData.bind(this);
    this.showTooltip = this.showTooltip.bind(this);
    this.removeTooltips = this.removeTooltips.bind(this);
  }

  showTooltip = (e) => {
    const target = parseInt(e.currentTarget.attributes.municipalityid.value);

    this.setState(() => {
      let isTooltipActive = { ...this.state.allTooltipsFalse };
      isTooltipActive[target] = true;
      return {
        isTooltipActive: isTooltipActive,
      };
    });
  };

  removeTooltips() {
    this.setState((previousState) => {
      return {
        isTooltipActive: { ...previousState.allTooltipsFalse },
      };
    });
  }

  componentDidUpdate() {
    if (this.props.dataReady) {
      if (!this.state.componentUpdated) {
        this.updateMapData();
      } else {
        let stateupdated =
          JSON.stringify(this.props.currentTech) ===
            JSON.stringify(this.state.currentTech) &&
          this.props.currentFilter === this.state.currentFilter &&
          this.props.currentMode === this.state.currentMode &&
          this.props.currentDateTime === this.state.currentDateTime;
        if (!stateupdated || !this.state.componentUpdated) {
          this.updateMapData();
        }
      }
    } else {
      if (this.state.componentUpdated) {
        this.setState({ componentUpdated: false });
      }
    }
  }

  updateMapData() {
    let valueScale = this.state.valueScale;
    let colorArray = this.props.colorArray;
    let colorScale = this.state.colorScale;
    let unitHour = this.state.unitHour;
    let unitMonth = this.state.unitMonth;

    // map js color function
    valueScale.range([0, colorArray.length - 1]);
    colorScale.range(
      colorArray.map(function (hex) {
        return d3.color(hex);
      }),
    );
    colorScale.domain(
      [...Array(colorArray.length).keys()].map(valueScale.invert),
    );

    let inw = 'inw';

    switch (this.props.currentFilter) {
      case 'inhabitants':
        unitHour = 'Wh/' + inw;
        unitMonth = 'kWh/' + inw;
        break;
      case 'surface':
        unitHour = 'MWh/km²';
        unitMonth = 'GWh/km²';
        break;
      default:
      case 'energy':
        unitHour = 'MWh';
        unitMonth = 'GWh';
    }

    const actualDate = new Date();
    const currentSelectedDate = new Date(this.props.currentDateTime);
    const hasForecast =
      currentSelectedDate.toDateString() === actualDate.toDateString() ||
      currentSelectedDate.getTime() > actualDate.getTime();

    valueScale
      .domain([
        0,
        d3.max(this.props.data, function (t) {
          return d3.max(Object.keys(t.values).map((key) => t.values[key]));
        }),
      ])
      .nice();
    colorScale.domain(
      [...Array(colorScale.domain().length).keys()].map(valueScale.invert),
    );

    this.setState((previousState, props) => {
      let isTooltipActive = { ...previousState.isTooltipActive };
      let allTooltipsFalse = { ...previousState.allTooltipsFalse };
      if (
        Object.keys(isTooltipActive).length === 0 &&
        isTooltipActive.constructor === Object
      ) {
        this.props.vlaanderen.objects.Refgem.geometries.map((geography) => {
          isTooltipActive[geography.properties.NISCODE] = false;
          return false;
        });
        allTooltipsFalse = isTooltipActive;
      }

      return {
        colorScale: colorScale,
        valueScale: valueScale,
        data: this.props.data,
        unitHour: unitHour,
        unitMonth: unitMonth,
        componentUpdated: true,
        currentDateTime: this.props.currentDateTime,
        dataReady: true,
        currentTech: this.props.currentTech,
        currentFilter: this.props.currentFilter,
        currentMode: this.props.currentMode,
        hasForecast: hasForecast,

        isTooltipActive: isTooltipActive,
        allTooltipsFalse: allTooltipsFalse,
      };
    });
  }

  render() {
    const { classes } = this.props;

    if (
      !this.props.dataReady ||
      !this.state.dataReady ||
      !this.state.componentUpdated
    ) {
      return (
        <div className={classes.chartDiv}>
          <CircularProgress className={classes.progress} size={50} />
        </div>
      );
    }

    let currentData;

    let currentDateTime = this.props.currentDateTime;
    currentData = this.state.data.filter(function (v) {
      return v.datetime.getTime() === new Date(currentDateTime).getTime();
    })[0].values;

    const hasForecast = this.state.hasForecast;
    const installedCapacities = this.props.capacities;
    const dayProductions = this.props.dayProductions;
    const uncutDayProductions = this.props.uncutDayProductions;
    const currentFilter = this.props.currentFilter;
    const currentMode = this.props.currentMode;

    let limitedFunctionalityWarning;
    switch (this.props.lang) {
      case 'nl':
      default:
        limitedFunctionalityWarning =
          'Internet Explorer ondersteunt niet alle mogelijkheden. Gebruik een andere browser voor een optimale ervaring.';
    }

    const unitHour = this.state.unitHour;
    const unitMonth = this.state.unitMonth;

    const lang = this.props.lang;

    let inhabitantAbbreviation;

    let isTooltipActive = this.state.isTooltipActive;

    return (
      <div className={classes.wrapperStyles}>
        <MapOffshoreIcon
          show={this.props.currentTech.windOffshore}
          currentTech={this.props.currentTech}
          currentMode={this.props.currentMode}
          currentFilter={this.props.currentFilter}
          data={this.props.offshoreData}
          maxValue={this.props.offshoreMaxValue}
          capacity={this.props.offshoreCapacity}
          dayProduction={this.props.offshoreDayProduction}
          currentDateTime={this.props.currentDateTime}
          dataReady={this.props.dataReady}
          colorArray={this.props.colorArray}
          yearSelected={this.props.yearSelected}
          isPlaying={this.props.playing}
          windowWidth={this.props.windowWidth}
          lang={this.props.lang}
          co2AvoidedFactor={this.props.co2AvoidedFactor}
          className={classes.offshoreIcon}
        />

        <MapLegend
          currentTech={this.props.currentTech}
          currentFilter={this.props.currentFilter}
          currentMode={this.props.currentMode}
          maxValue={this.props.maxValue}
          lang={this.props.lang}
        />
        {isIE && (
          <p className={classes.limitedFunctionalityWarning}>
            <WarningIcon
              style={{ verticalAlign: 'middle', marginRight: '10px' }}
            />
            {limitedFunctionalityWarning}
          </p>
        )}
        {this.state.dataReady ? (
          <>
            <ComposableMap
              projectionConfig={{
                scale: 13000,
              }}
              height={300}
              projection="geoMercator"
              className={classes.composableMap}
              onMouseLeave={this.removeTooltips}
            >
              <ZoomableGroup
                center={[4.2, 51.1]}
                zoom={1}
                minZoom={1}
                maxZoom={1}
              >
                <Geographies
                  geography={this.props.vlaanderen}
                  //disableOptimization // should only be removed if there are no updates to the map https://github.com/zcreativelabs/react-simple-maps/blob/master/README.md
                >
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.properties.NISCODE}
                        id={'municipality_' + geo.properties.NISCODE}
                        municipalityid={geo.properties.NISCODE}
                        onMouseEnter={this.showTooltip}
                        data-tip={
                          geo.properties.NAAM +
                          ' ' +
                          currentData[geo.properties.NISCODE]
                        }
                        data-for={String(geo.properties.NISCODE)}
                        geography={geo}
                        style={{
                          default: {
                            fill:
                              currentData[geo.properties.NISCODE] !==
                                undefined &&
                              currentData[geo.properties.NISCODE] !== 0
                                ? this.state.colorScale(
                                    currentData[geo.properties.NISCODE],
                                  )
                                : '#ddd',
                            fillOpacity: 1,
                            stroke: 'rgba(255,255,255,0.3)',
                            strokeWidth: 1.25,
                            outline: 'none',
                          },
                          hover: {
                            fill:
                              currentData[geo.properties.NISCODE] !==
                                undefined &&
                              currentData[geo.properties.NISCODE] !== 0
                                ? this.state.colorScale(
                                    currentData[geo.properties.NISCODE],
                                  )
                                : '#ddd',
                            fillOpacity: 0.5,
                            stroke: '#607D8B',
                            strokeWidth: 0.75,
                            outline: 'none',
                          },
                        }}
                      />
                    ))
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            {(!isIE || !this.props.isPlaying) &&
              this.props.vlaanderen.objects.Refgem.geometries.map(
                (geography) => {
                  const municipalityId = parseInt(
                    geography.properties.NISCODE,
                  );
                  const dayProduction = dayProductions[municipalityId];
                  const productionLastHour = currentData[municipalityId];
                  const uncutDayProduction =
                    uncutDayProductions[municipalityId];
                  const installedCapacity = installedCapacities[municipalityId];

                  return (
                    <MapTooltip
                      geography={geography}
                      key={municipalityId}
                      municipalityId={municipalityId}
                      lang={lang}
                      dayProduction={dayProduction}
                      productionLastHour={productionLastHour}
                      uncutDayProduction={uncutDayProduction}
                      installedCapacity={installedCapacity}
                      isActive={isTooltipActive[municipalityId]}
                      classes={classes}
                      currentData={currentData}
                      currentMode={this.props.currentMode}
                      currentFilter={this.props.currentFilter}
                      unitHour={unitHour}
                      unitMonth={unitMonth}
                      hasForecast={hasForecast}
                      yearSelected={this.props.yearSelected}
                      data={this.props.data}
                    ></MapTooltip>
                  );
                },
              )}
          </>
        ) : (
          <CircularProgress size={50} />
        )}
      </div>
    );
  }
}
EnergyMapFlanders.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withStyles(styles)(EnergyMapFlanders);
